import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SaveOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { Col, DatePicker, Input, Modal, Row, Select, Spin, Switch, Table, Tooltip } from "antd";
import { useFormik } from "formik";
import { cloneDeep, get, toLower } from "lodash";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../components/functional_components/Button/PFButton";
import { setLoading } from "../../redux/ducks/Loading";
import CreditCard from "../../services/api/CreditCard";
import { dbDate, langDate, paginationDefaults } from "../../utils/properties";
import { buildQueryString, exportFile, showError, showSuccess, trimData } from "../../utils/utilities";
import Filter from "../../components/functional_components/Filter/Filter";
import { setFilters } from "../../redux/ducks/Filters";
import User from "../../services/api/User";
import dayjs from "dayjs";
const { Option } = Select;

const CreditCards = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const dateFormat = langDate[toLower(currentLanguage)];
  const { confirm } = Modal;
  const filters = useSelector((state) => state.FiltersDuck.filters);
  const [state, setState] = useState({
    tableContent: [],
    pagination: {
      currentPage: paginationDefaults.page,
      size: paginationDefaults.size,
      totalItems: paginationDefaults.totalItems,
    },
    modal: {
      visible: false,
      type: "",
      data: {},
    },
    loading: false,
    hrList: [],
    hrSearchList: [],
    downloadLoading: false,
  });

  useEffect(() => {
    const queryString = buildQueryString(filters.obj, {});
    dispatch(setFilters(queryString));
    dataList(queryString.string);
  }, []);

  const formikCreditCard = useFormik({
    initialValues: {
      enabled: get(state.modal.data, "enabled", true),
      cardNumber: get(state.modal.data, "cardNumber", null),
      user: get(state.modal.data, "user.username", null),
      validFrom: get(state.modal.data, "validFrom", null) !== null ? dayjs(state.modal.data.validFrom, dbDate) : null,
      validTo: get(state.modal.data, "validTo", null) !== null ? dayjs(state.modal.data.validTo, dbDate) : null,
    },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (!values.cardNumber) {
        errors.cardNumber = "Required!";
      }
      if (!values.user) {
        errors.user = "Required!";
      }
      if (!values.validFrom) {
        errors.validFrom = "Required!";
      }
      if (!values.validTo) {
        errors.validTo = "Required!";
      }
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      setState((prevState) => ({ ...prevState, loading: true }));
      const data = {
        ...values,
        user: state.hrList.find((h) => h.username === values.user),
        id: state.modal.data?.id || undefined,
        validFrom: values.validFrom ? values.validFrom.format(dbDate) : null,
        validTo: values.validTo ? values.validTo.format(dbDate) : null,
      };
      const saveResponse = await CreditCard.setCreditCard(data);
      if (!saveResponse.error) {
        showSuccess();
        closeModal();
        const queryString = buildQueryString(filters.obj, {});
        dispatch(setFilters(queryString));
        dataList(queryString.string);
      } else {
        showError(get(saveResponse, "error.messageCode", "ER000"));
      }
      setState((prevState) => ({ ...prevState, loading: false }));
    },
  });

  const dataList = async (queryString) => {
    dispatch(setLoading(true));
    const { data, error, currentPage, pageSize, totalItems } = await CreditCard.getCreditCards(queryString);
    if (!error && data) {
      data.forEach((cc, i) => {
        cc.key = i; // This is necessary to remove warning in console for unique key row
      });
      setState((prevState) => ({
        ...prevState,
        tableContent: data || [],
        pagination: {
          currentPage: currentPage ? currentPage + 1 : paginationDefaults.page,
          size: pageSize || paginationDefaults.size,
          totalItems: totalItems || paginationDefaults.totalItems,
        },
      }));
    } else {
      showError(get(error, "messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const closeModal = () => {
    formikCreditCard.resetForm();
    setState((prevState) => ({ ...prevState, modal: { visible: false, type: "", data: {} } }));
  };

  const editCallback = async (id) => {
    setState((prevState) => ({ ...prevState, loading: true, modal: { visible: true, type: "edit" } }));
    const { data, error } = await CreditCard.getCreditCard(id);
    if (!error && data) {
      setState((prevState) => ({ ...prevState, hrList: [data.user], modal: { visible: true, type: "edit", data } }));
    } else {
      showError(get(error, "messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const deleteCallback = (id) => {
    confirm({
      title: t("general.ensureDelete"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        const deleteResponse = await CreditCard.deleteCreditCard(id);
        if (!deleteResponse.error) {
          showSuccess();
          const queryString = buildQueryString(filters.obj, {});
          dispatch(setFilters(queryString));
          dataList(queryString.string);
        } else {
          showError(get(deleteResponse, "error.messageCode", "ER000"));
        }
      },
      onCancel() {},
    });
  };

  const columns = [
    {
      title: t("creditCard.table.cardNumber"),
      dataIndex: "cardNumber",
      key: "cardNumber",
    },
    {
      title: t("creditCard.table.user"),
      dataIndex: "user",
      key: "user",
      render: (user) => `${user.name} ${user.surname}`,
    },
    {
      title: t("creditCard.table.enabled"),
      dataIndex: "enabled",
      key: "enabled",
      render: (enabled) => (enabled ? t("general.yes") : t("general.no")),
    },
    {
      title: t("general.actions"),
      key: "action",
      width: 80,
      render: (data) => {
        return (
          <div className="space-around">
            <div onClick={() => editCallback(data.id)}>
              <EditOutlined className="icon-medium" />
            </div>
            <div onClick={() => deleteCallback(data.id)}>
              <DeleteOutlined className="icon-medium" />
            </div>
          </div>
        );
      },
    },
  ];

  const onSearchusers = async (value, filter = false) => {
    if (value.length >= 2) {
      const { error, data } = await User.getUsersInternalHrList(`allPages=true&surname=${value}`);
      if (!error && data) {
        if (filter) {
          setState((prevState) => ({
            ...prevState,
            hrSearchList: data.map((u) => ({ value: u.username, label: `${u.name} ${u.surname}` })),
          }));
        } else {
          setState((prevState) => ({
            ...prevState,
            hrList: data,
          }));
        }
      }
    } else {
      setState((prevState) => ({ ...prevState, hrList: [], hrSearchList: [] }));
    }
  };

  const formikFilter = useFormik({
    initialValues: {
      cardNumber: "",
      user: "",
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      // When I search, I start my page to 0
      let searchValues = cloneDeep(values);
      searchValues["page"] = 0;
      searchValues["user.username"] = values.user;
      delete searchValues.user;
      const queryString = buildQueryString(filters.obj, searchValues);
      dispatch(setFilters(queryString));
      dataList(queryString.string);
    },
  });

  const fieldsFilter = [
    {
      type: "input",
      isRequired: false,
      id: "cardNumber",
      name: "cardNumber",
      value: formikFilter.values.cardNumber,
      label: t("creditCard.table.cardNumber"),
      onChange: formikFilter.handleChange,
      onBlur: formikFilter.handleBlur,
    },
    {
      type: "selectWithSearch",
      isRequired: false,
      id: "user",
      name: "user",
      value: formikFilter.values.user,
      label: t("creditCard.table.user") + " " + t("creditCard.userLabelInfo"),
      onChange: formikFilter.handleChange("user"),
      onBlur: formikFilter.handleBlur("user"),
      onSearch: (e) => onSearchusers(e, true),
      dataSelect: state.hrSearchList,
    },
  ];

  const resetFilter = () => {
    const queryString = buildQueryString({ page: paginationDefaults.page - 1, size: paginationDefaults.size }, {});
    dispatch(setFilters(queryString));
    dataList(queryString.string);
  };

  const downloadExport = async () => {
    setState((prevState) => ({ ...prevState, downloadLoading: true }));
    const exportResponse = await CreditCard.downloadExportFile();
    if (!exportResponse.error) {
      const blob = new Blob([exportResponse]);
      exportFile(blob, "Credit_Card.csv");
      showSuccess();
    } else {
      showError(get(exportResponse, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, downloadLoading: false }));
  };

  return (
    <>
      <Filter formikForm={formikFilter} fields={fieldsFilter} onReset={resetFilter} allInOneRow />
      <Row type="flex" align="middle" justify="end" className="mb-10">
        <Button
          currentIcon={<PlusOutlined className="icon-medium" />}
          clickCallback={() => setState((prevState) => ({ ...prevState, modal: { visible: true, type: "new" } }))}
        />
        <Button
          className="ml-10"
          isLoading={state.downloadLoading}
          currentIcon={<DownloadOutlined className="icon-medium" />}
          clickCallback={() => downloadExport()}
        />
      </Row>
      <Table
        dataSource={state.tableContent}
        columns={columns}
        size="small"
        pagination={{
          current: state.pagination.currentPage,
          pageSize: state.pagination.size,
          total: state.pagination.totalItems,
          onChange: (page, pageSize) => {
            const queryString = buildQueryString(filters.obj, { page: page - 1, size: pageSize });
            dispatch(setFilters(queryString));
            setState((prevState) => ({
              ...prevState,
              pagination: {
                currentPage: page,
                pageSize: pageSize,
                totalItems: state.pagination.totalItems,
              },
            }));
            dataList(queryString.string);
          },
        }}
      />
      <Modal
        open={state.modal.visible}
        width={700}
        title={
          state.modal.type === "edit" && state.modal.data && state.modal.data.brand
            ? state.modal.data.brand
            : t("creditCard.new")
        }
        footer={[
          <Button
            key="okButton"
            currentIcon={<SaveOutlined className="icon-medium" />}
            clickCallback={formikCreditCard.submitForm}
            isDisabled={!(formikCreditCard.dirty && formikCreditCard.isValid)}
          />,
        ]}
        onCancel={closeModal}
      >
        {state.loading ? (
          <div className="container-spinner-positioning">
            <Spin spinning={state.loading} className="spinner-positioning" />
          </div>
        ) : (
          ""
        )}
        <Row gutter={[40, 24]} justify="space-around">
          <Col lg={22} md={22} xs={24} sm={22}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="enabled">
                {t("creditCard.table.enabled")}*
              </label>
            </Row>
            <Row>
              <Switch
                id="enabled"
                name="enabled"
                onChange={(e) => {
                  formikCreditCard.setFieldValue("enabled", e);
                }}
                onBlur={formikCreditCard.handleBlur}
                checked={formikCreditCard.values.enabled}
                checkedChildren={t("general.yes")}
                unCheckedChildren={t("general.no")}
              />
            </Row>
          </Col>
        </Row>
        <Row gutter={[40, 24]} justify="space-around">
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="cardNumber">
                {t("creditCard.table.cardNumber")}*
                <Tooltip title={t("creditCard.cardNumberInfo")} className="ml-10">
                  <InfoCircleOutlined />
                </Tooltip>
              </label>
            </Row>
            <Row>
              <Input
                required
                id="cardNumber"
                name="cardNumber"
                maxLength={4}
                onChange={formikCreditCard.handleChange}
                onBlur={formikCreditCard.handleBlur}
                value={formikCreditCard.values.cardNumber}
              />
            </Row>
          </Col>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="user">
                {t("creditCard.table.user")}*
              </label>
            </Row>
            <Row>
              <p>{t("creditCard.userLabelInfo")}</p>
            </Row>
            <Row>
              <Select
                allowClear
                onClear={() => {
                  setState((prevState) => ({ ...prevState, hrList: [] }));
                }}
                className="w-100"
                id="user"
                optionFilterProp="value"
                name="user"
                showSearch
                placeholder={t("creditCard.table.user")}
                onChange={(value) => formikCreditCard.setFieldValue("user", value)}
                onBlur={() => {
                  formikCreditCard.handleBlur("user");
                }}
                onSearch={(value) => onSearchusers(value)}
                filterOption={(input, option) => option.name.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                value={formikCreditCard.values.user}
              >
                {state.hrList.length > 0 &&
                  state.hrList.map((user, idx) => {
                    return (
                      <Option key={idx} value={user.username} name={user.surname}>
                        {user.name} {user.surname}
                      </Option>
                    );
                  })}
              </Select>
            </Row>
          </Col>
        </Row>
        <Row gutter={[40, 24]} justify="space-around">
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="validFrom">
                {t("creditCard.table.validFrom")}*
              </label>
            </Row>
            <Row>
              <DatePicker
                id="validFrom"
                name="validFrom"
                format={dateFormat}
                locale={langDate[`locale_${toLower(currentLanguage)}`]}
                value={formikCreditCard.values.validFrom}
                onChange={(date) => formikCreditCard.setFieldValue("validFrom", date)}
                disabledDate={(current) => {
                  // Set the minimum and maximum allowed dates
                  const maxDate = cloneDeep(formikCreditCard.values.validTo);
                  // Disable all dates after maxDate
                  return maxDate && current && current > maxDate;
                }}
              />
            </Row>
          </Col>
          <Col lg={10} md={10} xs={24} sm={10}>
            <Row>
              <label className="fw-bold mt-10" htmlFor="validTo">
                {t("creditCard.table.validTo")}*
              </label>
            </Row>
            <Row>
              <DatePicker
                id="validTo"
                name="validTo"
                format={dateFormat}
                locale={langDate[`locale_${toLower(currentLanguage)}`]}
                value={formikCreditCard.values.validTo}
                onChange={(date) => formikCreditCard.setFieldValue("validTo", date)}
                disabledDate={(current) => {
                  // Set the minimum and maximum allowed dates
                  const minDate = cloneDeep(formikCreditCard.values.validFrom);
                  // Disable all dates before minDate and after maxDate, except maxDate itself
                  return current && current < minDate;
                }}
              />
            </Row>
          </Col>
        </Row>
      </Modal>
    </>
  );
};

export default CreditCards;
